<template>
  <div class="bulanan-penengah">
    <v-snackbar
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      v-model="snackbar.show"
    >
      {{ snackbar.message }}
    </v-snackbar>

    <PageHeader
      icon="mdi-chart-bell-curve"
      title="Pendapatan Isi Rumah Kasar Bulanan Penengah"
      subtitle="Mengikut strata, kaum dan daerah pentadbiran"
    />

    <!-- Detailed table -->
    <v-card outlined class="mb-4">
      <v-card-title class="py-3 d-flex align-center flex-wrap">
        <v-icon small class="mr-2">mdi-table</v-icon>
        <span class="subtitle-1 font-weight-medium">Pendapatan Isi Rumah Bulanan Penengah</span>
        <v-chip x-small color="grey lighten-3" class="ml-3 font-weight-medium">{{ scopeName }}</v-chip>
        <v-spacer></v-spacer>
        <v-btn color="green darken-2" dark class="mr-2 text-none" :disabled="!hasData" :loading="exporting" @click="exportExcel">
          <v-icon left>mdi-microsoft-excel</v-icon>
          Excel
        </v-btn>
        <v-btn color="red darken-1" dark class="text-none" :disabled="!hasData" :loading="exportingPdf" @click="exportPDF">
          <v-icon left>mdi-file-pdf-box</v-icon>
          PDF
        </v-btn>
      </v-card-title>

      <!-- Filters (same layout as Kadar Respon / Bulanan Purata) -->
      <!-- Setiap penapis memakai susunan yang SAMA: label eyebrow di atas,
           kawalan di bawah. Sebelum ini Negeri memakai label terapung v-select
           manakala Pusingan/Data memakai kapsyen di sebelah kiri — tiga kawalan,
           tiga cara melabel. `align="end"` menjajarkan semua kawalan pada garis
           dasar yang sama walaupun tinggi labelnya berbeza. -->
      <v-card-text class="filter-bar py-3">
        <v-row align="end" dense>
          <v-col cols="12" sm="3">
            <div class="fld__label">Negeri</div>
            <v-select
              v-model="negeri"
              :items="negeriList"
              :disabled="negeriLocked"
              outlined dense hide-details
            ></v-select>
          </v-col>
          <!-- <v-col cols="12" sm="3">
            <v-select
              v-model="daerah"
              :items="daerahSelectItems"
              label="Daerah Pentadbiran"
              :disabled="negeri === 'ALL'"
              multiple outlined dense hide-details clearable
            >
              <template v-slot:selection="{ index }">
                <span v-if="index === 0" class="caption text--secondary">{{ daerahLabel }}</span>
              </template>
            </v-select>
          </v-col> -->
          <v-col cols="12" sm="auto">
            <div class="fld__label">Pusingan</div>
            <div class="d-flex align-center pusingan-range">
              <v-select v-model="roundRange" :items="roundRangeItems" outlined dense hide-details class="pusingan-sel"></v-select>
            </div>
          </v-col>
          <!-- <v-col cols="12" sm="3">
            <div class="d-flex align-center" style="height:40px">
              <span class="text-caption text--secondary mr-3" style="white-space:nowrap">Sampel</span>
              <v-btn-toggle v-model="sampel" mandatory color="primary" class="data-toggle">
                <v-btn value="1" height="40" class="text-none px-4">1</v-btn>
                <v-btn value="2" height="40" class="text-none px-4">2</v-btn>
                <v-btn value="3" height="40" class="text-none px-4">Semua</v-btn>
              </v-btn-toggle>
            </div>
          </v-col> -->
          <!-- Data weighting toggle — UI only for now; wire to backend later -->
          <v-col cols="12" sm="auto">
            <div class="fld__label">Data</div>
            <!-- `color="primary"` digugurkan: primary Vuetify = biru, yang
                 bertentangan dengan sistem teal. Keadaan aktif diwarnakan
                 melalui CSS (.data-toggle) sebaliknya. -->
            <!-- 38, bukan 40: + birai 1px atas/bawah = tepat 40px, sama tinggi
                 dengan medan outlined di sebelahnya, supaya label "DATA" duduk
                 pada garis yang sama dengan "NEGERI"/"PUSINGAN". -->
            <v-btn-toggle v-model="dataWajaran" mandatory class="data-toggle">
              <v-btn value="W" height="38" class="text-none px-4">Wajaran</v-btn>
              <v-btn value="TW" height="38" class="text-none px-4">Tanpa Wajaran</v-btn>
            </v-btn-toggle>
          </v-col>
          <v-spacer></v-spacer>
          <v-col cols="12" sm="auto">
            <v-btn outlined height="40" class="text-none reset-btn" @click="resetFilters">
              <v-icon small left>mdi-refresh</v-icon>
              Tetapkan Semula
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text class="d-flex align-center flex-wrap py-2">
        <v-spacer></v-spacer>
        <span class="penapis-ringkas text--secondary">{{ filterSummary }}</span>
      </v-card-text>

      <!-- Empty state: no records for the current filters -->
      <div v-if="loading" class="empty-state text-center py-12 px-4">
        <v-progress-circular indeterminate color="primary" size="48" width="4"></v-progress-circular>
        <div class="subtitle-1 font-weight-medium grey--text text--darken-1 mt-3">Memuatkan data…</div>
      </div>
      <div v-else-if="!hasData" class="empty-state text-center py-12 px-4">
        <v-icon size="56" color="grey lighten-1">mdi-database-off-outline</v-icon>
        <div class="subtitle-1 font-weight-medium grey--text text--darken-1 mt-3">Tiada rekod dijumpai</div>
        <div class="penapis-ringkas grey--text mt-1">Tiada data bagi penapis semasa ({{ filterSummary }}). Sila ubah pilihan penapis.</div>
      </div>

      <template v-else>
      <v-simple-table class="bp-table">
        <thead>
          <tr>
            <th class="cat" rowspan="2">Kategori</th>
            <th v-for="y in YEARS" :key="'h' + y" rowspan="2">{{ y }} (RM)</th>
            <th :colspan="cagrColCount">Kadar Pertumbuhan Tahunan Dikompaun (CAGR) (%)</th>
          </tr>
          <tr>
            <template v-if="hasCagr">
              <th v-for="p in cagrPairs" :key="'hp' + p.from + p.to">{{ p.from }} - {{ p.to }}</th>
            </template>
            <!-- single cycle: nothing to compare yet → placeholder column -->
            <th v-else>—</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="r in visibleRows">
          <tr v-if="r._isGroup" :key="r.state + '-g'" class="group">
            <td class="cat" :colspan="colCount">{{ r.state }}</td>
          </tr>
          <tr v-else-if="r._isSpacer" :key="r.state + '-s'" class="spacer">
            <td :colspan="colCount"></td>
          </tr>
          <tr v-else :key="r.state" :class="{ total: r._isTotal, sub: r._isStrata || r._isKaum || r._isDaerah }">
            <td class="cat" :class="{ indent: r.indent }">{{ r.state }}</td>
            <td v-for="y in YEARS" :key="'v' + y">{{ rm(r.values[y]) }}</td>
            <template v-if="hasCagr">
              <td v-for="p in cagrPairs" :key="'c' + p.from + p.to">{{ pf(r.cagr[p.from + '-' + p.to]) }}</td>
            </template>
            <td v-else class="cagr-empty">—</td>
          </tr>
          </template>
        </tbody>
      </v-simple-table>

      <div class="foot penapis-ringkas text--secondary px-4 py-3 d-flex justify-space-between flex-wrap">
        <span>{{ scopeName }}</span>
        <span>{{ filterSummary }}</span>
      </div>
      </template>
    </v-card>
  </div>
</template>

<script>
import { addJataHeaderExcel, addJataHeaderPdf } from "@/services/exportHeader";
import { T, loadTheme, argb, rgb, dark } from "@/services/theme";
import hiesWatermark from "@/assets/HIES watermark.png";
import PendapatanPenengahServices from "@/services/pendapatanPenengah";
import { surveyTitle } from "@/services/surveyYear";
import { negeriStore, loadNegeriList } from "@/services/negeriList";
import getPusingan from "@/services/pusingan";
import { negeriLock } from "@/services/negeriScope";

const svc = new PendapatanPenengahServices();
const pusinganSvc = new getPusingan();

/* ============ DATA MODEL (mock — wire to a real endpoint later) ============ */
// Negeri dropdown labels come from lookupnegeri via the shared negeriList
// service (negeriStore.list — reactive, cached, with offline fallback).


/* ============ SURVEY YEARS ============
   Years come from the backend (the distinct TAHUN values present in jr2).
   The table header, data columns, CAGR columns and both exports all rebuild
   from `this.years`, so adding a survey year to the database makes it appear
   here automatically. */

// build the CAGR pair list from an ordered array of year numbers
function cagrPairsFrom(years) {
  return years.slice(0, -1).map((from, i) => {
    const to = years[i + 1];
    return { from, to, years: to - from };
  });
}

// CAGR % between two years over n years: (ln(end/start)/n)*100 — matches the Excel.
// Returns null (→ rendered as a dash) when either endpoint is missing/null, so a
// pair that touches an empty cycle (e.g. 2022) shows "—" instead of a bogus 0%.
function cagr(start, end, years) {
  if (start == null || end == null || start <= 0 || end <= 0) return null;
  return (Math.log(end / start) / years) * 100;
}

// derive the CAGR map ({ "2024-2025": 3.2, ... }) for a row's per-year values
function cagrMap(values, pairs) {
  const out = {};
  pairs.forEach(p => (out[p.from + "-" + p.to] = cagr(values[p.from], values[p.to], p.years)));
  return out;
}

// 1-based column index -> Excel column letter (1 -> "A", 27 -> "AA")
function colLetter(n) {
  let s = "";
  while (n > 0) {
    const r = (n - 1) % 26;
    s = String.fromCharCode(65 + r) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if ([...document.scripts].some(s => s.src === src)) return resolve();
    const el = document.createElement("script");
    el.src = src;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error("Gagal memuatkan " + src));
    document.head.appendChild(el);
  });
}

// load an image asset → { dataUrl(PNG base64), width, height }
// alpha (0-1, optional): bake transparency into the pixels — used for watermarks,
// since jsPDF's addImage has no opacity option of its own
function loadImage(url, alpha) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const c2d = canvas.getContext("2d");
      if (alpha != null) c2d.globalAlpha = alpha;
      c2d.drawImage(img, 0, 0);
      let dataUrl;
      try { dataUrl = canvas.toDataURL("image/png"); } catch (e) { dataUrl = url; }
      resolve({ dataUrl, width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = reject;
    img.src = url;
  });
}

export default {
  name: "BulananPenengah",
  data() {
    return {
      svc,
      // years + derived CAGR pairs come from the API response
      years: [],
      cagrPairs: [],
      api: {},
      loading: true,   // start loading — page fetches on created()
      snackbar: { show: false, message: "", color: "success", timeout: 3000 },
      negeri: "ALL",          // negeriID ("01".."16") or "ALL"
      negeriLocked: false,    // true for PP/PO/FE → negeri pinned to own kodneg
      daerah: [],             // selected daerah DP codes (e.g. ["03","04"])
      sampel: "3",            // "1" / "2" / "3" (Semua)
      dataWajaran: "W",       // "W" (Wajaran) / "TW" (Tanpa Wajaran) — UI only, wire to backend later
      // Active pusingan only — loaded in created() from getAllPusingan() filtered
      // by status == 1 (same rule as the Wajaran / Tapis Pusingan view). Falls back
      // to 1..12 if the service is unavailable.
      roundItems: [],
      // Pusingan range filter (From–To). Defaults to first–last available in
      // loadActiveRounds(). selectedRoundsModel is derived from this range.
      roundFrom: null,
      roundTo: null,
      exporting: false,
      exportingPdf: false,
    };
  },
  computed: {
    todayStr() {
      return new Date().toISOString().slice(0, 10);
    },
    YEARS() {
      return this.years;
    },
    // CAGR needs ≥2 cycles to compare; with one (or zero) there are no pairs, so
    // we show a single placeholder column with "—" instead of a 0-width header.
    hasCagr() {
      return this.cagrPairs.length > 0;
    },
    cagrColCount() {
      return Math.max(1, this.cagrPairs.length);
    },
    // total column count: Kategori + one per year + CAGR columns (≥1)
    colCount() {
      return 1 + this.years.length + this.cagrColCount;
    },
    // true when there are survey years to show — drives the table vs empty-state
    hasData() {
      return this.years.length > 0;
    },
    negeriList() {
      // Locked roles (PP/PO/FE) only see their own negeri — no Malaysia/other options.
      if (this.negeriLocked) {
        return negeriStore.list.filter(n => n.id === this.negeri).map(n => ({ text: n.nama, value: n.id }));
      }
      return [{ text: "Malaysia", value: "ALL" }, ...negeriStore.list.map(n => ({ text: n.nama, value: n.id }))];
    },
    negeriName() {
      const map = {};
      negeriStore.list.forEach(n => (map[n.id] = n.nama));
      return map;
    },
    // dependent daerah dropdown items for the selected negeri (from API daerahList)
    daerahSelectItems() {
      if (this.negeri === "ALL") return [];
      return (this.api.daerahList || []).map(d => ({ text: d.namaDP || d.dp, value: d.dp }));
    },
    daerahName() {
      const map = {};
      (this.api.daerahList || []).forEach(d => (map[d.dp] = d.namaDP || d.dp));
      return map;
    },
    // compact label for the daerah multi-select chip area
    daerahLabel() {
      const n = this.daerah.length;
      if (n === 0) return "";
      if (n === 1) return this.daerahName[this.daerah[0]] || this.daerah[0];
      if (n === this.daerahSelectItems.length) return "Semua Daerah";
      return `${n} daerah dipilih`;
    },
    // The selected pusingan = every available round within the From–To range.
    // Derived from roundFrom/roundTo so the rest of the page (fetch param,
    // summary, exports) keeps working unchanged.
    selectedRoundsModel() {
      if (this.roundFrom == null || this.roundTo == null) return [];
      const lo = Math.min(this.roundFrom, this.roundTo);
      const hi = Math.max(this.roundFrom, this.roundTo);
      return this.roundItems.filter(p => p >= lo && p <= hi);
    },
    selectedRounds() {
      return [...this.selectedRoundsModel].sort((a, b) => a - b);
    },
    allRounds() {
      return this.roundItems.length > 0 && this.selectedRoundsModel.length === this.roundItems.length;
    },
    // Pusingan sentiasa julat berlabuh pada pusingan pertama — pengguna hanya
    // memilih hujungnya (01 - 01 … 01 - 12), jadi permulaan 02 mustahil dari binaan.
    roundRangeItems() {
      const pad = n => String(n).padStart(2, "0");
      const lo = this.roundItems.length ? this.roundItems[0] : null;
      return this.roundItems.map(p => ({ text: `${pad(lo)} - ${pad(p)}`, value: p }));
    },
    // One dropdown, so the start can never drift off the first pusingan.
    roundRange: {
      get() { return this.roundTo; },
      set(v) {
        if (this.roundItems.length) this.roundFrom = this.roundItems[0];
        this.roundTo = v;
      },
    },
    ddLabel() {
      if (this.allRounds) return "Semua Pusingan";
      if (!this.selectedRoundsModel.length) return "Tiada dipilih";
      const lo = this.selectedRounds[0];
      const hi = this.selectedRounds[this.selectedRounds.length - 1];
      return lo === hi ? `Pusingan ${lo}` : `Pusingan ${lo}–${hi}`;
    },
    // sampel filter no longer shown — replaced by the Wajaran / Tanpa Wajaran toggle
    // sampelLabel() {
    //   return this.sampel === "3" ? "Semua Sampel" : `Sampel ${this.sampel}`;
    // },
    // Wajaran / Tanpa Wajaran label for the summary + exports.
    wajaranLabel() {
      return this.dataWajaran === "TW" ? "Tanpa Wajaran" : "Wajaran";
    },
    // Meaningful export filename (no extension) built from the active filters, e.g.
    //   Bulanan_Penengah_Johor_Pusingan1_Wajaran_20260618
    // Pusingan slug for filenames: always a range — "Pusingan2" or "Pusingan1-12"
    // (incl. when all rounds are selected). Falls back to the full available range.
    pusinganSlug() {
      const rounds = this.selectedRounds.length ? this.selectedRounds : this.roundItems;
      if (!rounds.length) return "Pusingan";
      const lo = rounds[0];
      const hi = rounds[rounds.length - 1];
      return "Pusingan" + (lo === hi ? `${lo}` : `${lo}-${hi}`);
    },
    exportFileName() {
      const parts = ["Pendapatan_Isi_Rumah_Bulanan_Penengah"];
      if (this.negeri !== "ALL") parts.push((this.scopeName || "").replace(/[^A-Za-z0-9]+/g, ""));
      parts.push(this.pusinganSlug);
      parts.push(this.dataWajaran === "TW" ? "TanpaWajaran" : "Wajaran");
      parts.push(this.todayStr.replace(/-/g, ""));
      return parts.join("_");
    },
    scopeName() {
      if (this.negeri === "ALL") return "Malaysia";
      const neg = this.negeriName[this.negeri] || this.negeri;
      if (this.daerah.length === 1) return `${neg} — ${this.daerahName[this.daerah[0]] || this.daerah[0]}`;
      if (this.daerah.length > 1) return `${neg} — ${this.daerah.length} daerah`;
      return neg;
    },
    // attach a derived CAGR map to an API row ({ state, values })
    withCagr() {
      const pairs = this.cagrPairs;
      return row => ({ ...row, cagr: cagrMap(row.values || {}, pairs) });
    },
    /* ---- table rows (built from the API payload) ----
       Malaysia view : Malaysia → Negeri (list)
       Negeri  view  : Negeri  → Strata → Kaum → Daerah Pentadbiran (list)  */
    visibleRows() {
      const items = [];
      const isAll = this.negeri === "ALL";
      const wc = this.withCagr;

      if (isAll) {
        const negeriRows = (this.api.perNegeri || []).map(wc);
        // MALAYSIA total comes straight from the API (negxdaerah NG/DP null for past
        // years + the whole hid_summary for the latest year). Fall back to the simple
        // mean of the per-negeri figures only if the API didn't supply it.
        const header = this.api.malaysiaTotal || this.aggregateRows(negeriRows);
        items.push({ _isTotal: true, state: "MALAYSIA", ...wc(header) });

        // Malaysia-wide Strata + Kaum (from the API, computed across all negeri)
        const strata = (this.api.strata || []).map(wc);
        if (strata.length) {
          items.push({ _isGroup: true, state: "Strata" });
          strata.forEach(r => items.push({ _isStrata: true, ...r }));
          items.push({ _isSpacer: true, state: "__spacer1__" });
        }

        const kaum = (this.api.kaum || []).map(wc);
        if (kaum.length) {
          items.push({ _isGroup: true, state: "Kaum" });
          kaum.forEach(r => items.push({ _isKaum: true, ...r }));
          items.push({ _isSpacer: true, state: "__spacer2__" });
        }

        if (negeriRows.length) {
          items.push({ _isGroup: true, state: "Negeri" });
          negeriRows.forEach(r => items.push({ _isNegeri: true, ...r }));
        }
      } else {
        const total = this.api.scopeTotal ? wc(this.api.scopeTotal) : null;
        items.push({
          _isTotal: true,
          state: this.scopeName,
          values: (total && total.values) || {},
          cagr: (total && total.cagr) || {},
        });

        const strata = (this.api.strata || []).map(wc);
        if (strata.length) {
          items.push({ _isGroup: true, state: "Strata" });
          strata.forEach(r => items.push({ _isStrata: true, ...r }));
          items.push({ _isSpacer: true, state: "__spacer1__" });
        }

        const kaum = (this.api.kaum || []).map(wc);
        if (kaum.length) {
          items.push({ _isGroup: true, state: "Kaum" });
          kaum.forEach(r => items.push({ _isKaum: true, ...r }));
          items.push({ _isSpacer: true, state: "__spacer2__" });
        }

        const daerah = (this.api.daerah || []).map(wc);
        if (daerah.length) {
          items.push({ _isGroup: true, state: "Daerah Pentadbiran" });
          daerah.forEach(r => items.push({ _isDaerah: true, ...r }));
        }
      }
      return items;
    },
    filterSummary() {
      const rd = this.allRounds
        ? "Semua pusingan"
        : "Pusingan " + this.selectedRounds.join(",");
      return `${this.negeri === "ALL" ? "Malaysia" : this.scopeName} • ${rd} • ${this.wajaranLabel}`;
    },
  },
  watch: {
    negeri() {
      this.daerah = [];
      this.queueFetch();
    },
    daerah() {
      this.queueFetch();
    },
    sampel() {
      this.queueFetch();
    },
    // Wajaran / Tanpa Wajaran toggle changes the median type → refetch
    dataWajaran() {
      this.queueFetch();
    },
    // keep From <= To: raising From past To drags To along
    roundFrom(v) {
      if (v != null && this.roundTo != null && this.roundTo < v) this.roundTo = v;
    },
    // selectedRoundsModel is a computed derived from the From–To range; watching it
    // refetches whenever either bound changes
    selectedRoundsModel() {
      this.queueFetch();
    },
  },
  async created() {
    loadNegeriList(); // refresh negeri names from lookupnegeri (shared cache)
    // Restricted roles (PP/PO/FE) are pinned to their own negeri — lock the selector
    // and default it to their kodneg before the first fetch. (The backend enforces
    // this too, so the data is scoped regardless; this is the matching UI.)
    const lock = negeriLock(this.$store);
    if (lock.locked) {
      this.negeri = lock.kodneg;
      this.negeriLocked = true;
    }
    // populate the active-pusingan filter first (sets selectedRoundsModel, which
    // the watcher coalesces into a single fetch), then load the table data
    await this.loadActiveRounds();
    this.fetchData();
  },
  methods: {
    /* ---- data ---- */
    queueFetch() {
      clearTimeout(this._fetchTimer);
      this._fetchTimer = setTimeout(() => this.fetchData(), 0);
    },
    async fetchData() {
      this.loading = true;
      try {
        const params = {
          // empty selection is an explicit "none" (not "all") → send a marker the
          // backend distinguishes from an absent param. All 12 → "" (= all).
          P: this.selectedRoundsModel.length === 0 ? "__NONE__" : this.selectedRounds.join(","),
          S: this.sampel,
          W: this.dataWajaran,                 // "W" = Wajaran (weighted median), "TW" = Tanpa Wajaran
        };
        if (this.negeri !== "ALL") params.NG = this.negeri;
        if (this.negeri !== "ALL" && this.daerah.length) params.DP = this.daerah.join(",");

        const d = await this.svc.getPendapatanPenengah(params);
        this.years = (d.years || []).map(Number);
        this.cagrPairs = cagrPairsFrom(this.years);
        this.api = d;
      } catch (err) {
        // No data for the current filters (or a backend hiccup) → show zeros
        // instead of an error. The empty payload renders an all-zero table.
        this.api = {};
        this.years = [];
        this.cagrPairs = [];
      }
      this.loading = false;
    },
    // mean of a list of API rows per year — used for the MALAYSIA header row.
    // (A pooled median can't be derived from per-negeri medians, so we show the
    //  mean of the per-negeri medians as a headline figure.)
    aggregateRows(rows) {
      const values = {};
      this.years.forEach(y => {
        const vals = rows.map(r => (r.values && r.values[y]) || 0).filter(v => v > 0);
        values[y] = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
      });
      return { values };
    },
    /* ---- format ---- */
    rm(n) {
      if (n === null || n === undefined) return "—";
      return new Intl.NumberFormat("en-MY").format(Math.round(n));
    },
    pf(n) {
      if (n === null || n === undefined) return "—";
      return n.toFixed(1);
    },
    /* ---- filters ---- */
    // select the full available range (first–last available pusingan)
    selectFullRange() {
      if (!this.roundItems.length) return;
      this.roundFrom = this.roundItems[0];
      this.roundTo = this.roundItems[this.roundItems.length - 1];
    },
    resetFilters() {
      // Locked roles (PP/PO/FE) stay pinned to their own negeri — "ALL" is not in
      // their negeriList, so resetting to it would blank the select and mislabel
      // the scope (chip/exports would read "Malaysia").
      if (!this.negeriLocked) this.negeri = "ALL";
      this.daerah = [];
      this.sampel = "3";
      this.selectFullRange();
    },
    // Load the active pusingan (status == 1) and default-select them all. Mirrors
    // the Wajaran view's getAllPusingan() + status filter. On any failure we fall
    // back to 1..12 so the filter is never empty.
    async loadActiveRounds() {
      let rounds = Array.from({ length: 12 }, (_, i) => i + 1);
      try {
        const res = await pusinganSvc.getAllPusingan();
        const rows = (res && res.data && res.data.data) || [];
        const active = rows
          .filter(r => r.status == "1" || r.status == 1)
          .map(r => parseInt(r.pusingan, 10))
          .filter(n => !isNaN(n));
        const uniq = [...new Set(active)].sort((a, b) => a - b);
        if (uniq.length) rounds = uniq;
      } catch (e) {
        console.error("loadActiveRounds failed:", e);
      }
      this.roundItems = rounds;
      this.selectFullRange();
    },
    /* ---- styled .xlsx export ---- */
    async exportExcel() {
      this.exporting = true;
      try {
        await loadTheme();
        await loadScript("/js/exceljs.min.js");
        const ExcelJS = window.ExcelJS;

        const NAVY = argb(T.tajukLaporan), HEAD = argb(T.kepalaJadual), NAVY2 = argb(dark(T.kepalaJadual)), TEAL = argb(T.graf), WHITE = "FFFFFFFF",
              GROUP = "FFE8EEF3", SPACER = "FF475569", SUB = "FFF4F8FA", LINE = "FFE2E8F0";
        const thin = { style: "thin", color: { argb: LINE } };
        const border = { top: thin, left: thin, bottom: thin, right: thin };
        const center = { vertical: "middle", horizontal: "center", wrapText: true };
        const fill = argb => ({ type: "pattern", pattern: "solid", fgColor: { argb } });

        // column geometry — all derived from the API years / cagr pairs
        const YEARS = this.years;
        // Effective CAGR columns: real year-pairs, or a single "—" placeholder
        // when there's only one cycle (nothing to compare). Never zero-width.
        const CAGR_COLS = this.cagrPairs.length
          ? this.cagrPairs.map(p => ({ label: `${p.from} - ${p.to}`, value: row => row.cagr[p.from + "-" + p.to] }))
          : [{ label: "—", value: () => null }];
        const dataCols = YEARS.length + CAGR_COLS.length;   // value columns (excl. Kategori)
        const nCols = 1 + dataCols;                  // total columns
        const L = colLetter;                         // 1 -> "A", 2 -> "B", ...
        const MIN_BANNER_COLS = 6;
        const bannerCols = Math.max(nCols, MIN_BANNER_COLS);
        const last = L(nCols);                       // last DATA column letter
        const bannerLast = L(bannerCols);            // last banner column letter
        const firstCagrIdx = 2 + YEARS.length;       // 1-based index of first CAGR column

        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet("Bulanan Penengah", { views: [{ showGridLines: false }] });
        const colWidths = [{ width: 26 }, ...YEARS.map(() => ({ width: 16 })), ...CAGR_COLS.map(() => ({ width: 14 }))];
        while (colWidths.length < bannerCols) colWidths.push({ width: 12 });
        ws.columns = colWidths;

        // ── Logo row 1: Jata Negara crest + config-driven title (services/exportHeader) ──
        await addJataHeaderExcel(wb, ws, colWidths, bannerLast);

        // ── Title block (rows 2-3, below the logo) ───────────────────────────
        ws.mergeCells(`A2:${bannerLast}2`);
        Object.assign(ws.getCell("A2"), {
          value: await surveyTitle(),
          alignment: center, font: { bold: true, size: 12, color: { argb: WHITE } }, fill: fill(NAVY),
        });
        ws.getRow(2).height = 24;

        ws.mergeCells(`A3:${bannerLast}3`);
        Object.assign(ws.getCell("A3"), {
          value: "PENDAPATAN ISI RUMAH BULANAN PENENGAH",
          alignment: center, font: { bold: true, size: 11, color: { argb: WHITE } }, fill: fill(TEAL),
        });
        ws.getRow(3).height = 20;

        // Filter summary — ONE line spanning the full width (matches the PDF
        // "Pilihan: … | Pusingan: … | Data: …" single-line layout). Never overflows.
        ws.mergeCells(`A5:${bannerLast}5`);
        Object.assign(ws.getCell("A5"), {
          value: `Pilihan: ${this.negeri === "ALL" ? "Malaysia" : this.scopeName}     |     Pusingan: ${this.ddLabel}     |     Data: ${this.wajaranLabel}`,
          font: { size: 10 }, alignment: { vertical: "middle", horizontal: "left" },
        });
        ws.getRow(5).height = 18;

        // Grouped header rows 7-8 (after the logo + title block + 1-line summary)
        const HEAD1 = 7, HEAD2 = 8;
        const setH = (cell, text, bg) => Object.assign(ws.getCell(cell), {
          value: text, alignment: center, border,
          font: { bold: true, size: 10, color: { argb: WHITE } }, fill: fill(bg || HEAD),
        });
        ws.mergeCells(`A${HEAD1}:A${HEAD2}`); setH(`A${HEAD1}`, "KATEGORI");
        YEARS.forEach((y, i) => {
          const col = L(2 + i);
          ws.mergeCells(`${col}${HEAD1}:${col}${HEAD2}`);
          setH(`${col}${HEAD1}`, `${y} (RM)`);
        });
        const cagrFirst = L(firstCagrIdx);
        ws.mergeCells(`${cagrFirst}${HEAD1}:${last}${HEAD1}`);
        setH(`${cagrFirst}${HEAD1}`, "KADAR PERTUMBUHAN TAHUNAN DIKOMPAUN (CAGR) (%)");
        CAGR_COLS.forEach((c, i) => setH(`${L(firstCagrIdx + i)}${HEAD2}`, c.label, NAVY2));
        ws.getRow(HEAD1).height = 34;
        ws.getRow(HEAD2).height = 20;

        // Data rows start right after the header
        let rowIdx = HEAD2 + 1;
        this.visibleRows.forEach(r => {
          const row = ws.getRow(rowIdx);
          if (r._isGroup) {
            ws.mergeCells(`A${rowIdx}:${last}${rowIdx}`);
            Object.assign(ws.getCell(`A${rowIdx}`), {
              value: (r.state || "").toUpperCase(), border,
              alignment: { vertical: "middle", horizontal: "left" },
              font: { bold: true, size: 10, color: { argb: "FF0B1F3A" } }, fill: fill(GROUP),
            });
            row.height = 18; rowIdx++; return;
          }
          if (r._isSpacer) {
            ws.mergeCells(`A${rowIdx}:${last}${rowIdx}`);
            Object.assign(ws.getCell(`A${rowIdx}`), { fill: fill(SPACER) });
            row.height = 5; rowIdx++; return;
          }
          const isTotal = !!r._isTotal;
          const isSub = !!(r._isStrata || r._isKaum || r._isDaerah);
          // state, then one cell per year, then one per CAGR column (placeholder → null)
          const vals = [
            r.state,
            ...YEARS.map(y => r.values[y]),
            ...CAGR_COLS.map(c => c.value(r)),
          ];
          vals.forEach((val, ci) => {
            const cell = row.getCell(ci + 1);
            const isCagrCol = ci >= firstCagrIdx - 1;       // 0-based threshold for CAGR columns
            // any value column (RM or CAGR) renders "—" when null/missing
            if (ci !== 0 && (val === null || val === undefined)) cell.value = "—";
            else if (typeof val === "number") cell.value = isCagrCol ? Math.round(val * 10) / 10 : Math.round(val);
            else cell.value = val;
            cell.border = border;
            cell.alignment = ci === 0 ? { vertical: "middle", horizontal: "left", indent: r.indent ? 3 : isSub ? 1 : 0 } : { vertical: "middle", horizontal: "right" };
            cell.font = { size: 11, bold: ci === 0 && !isSub };
            if (ci >= 1 && !isCagrCol && typeof cell.value === "number") cell.numFmt = '#,##0';
            if (isCagrCol && typeof cell.value === "number") cell.numFmt = '0.0';
          });
          if (isTotal) {
            for (let c = 1; c <= nCols; c++) { row.getCell(c).fill = fill(TEAL); row.getCell(c).font = { size: 11, bold: true, color: { argb: WHITE } }; }
          } else if (isSub) {
            for (let c = 1; c <= nCols; c++) row.getCell(c).fill = fill(SUB);
          }
          rowIdx++;
        });

        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `${this.exportFileName}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        this.snackbar = { show: true, message: err.message || "Eksport Excel gagal.", color: "error", timeout: 4000 };
      }
      this.exporting = false;
    },

    /* ---- styled PDF export ---- */
    async exportPDF() {
      this.exportingPdf = true;
      try {
        await loadTheme();
        await loadScript("/js/jspdf.umd.min.js");
        await loadScript("/js/jspdf.plugin.autotable.min.js");
        const { jsPDF } = window.jspdf;
        const NAVY = rgb(T.tajukLaporan), HEAD = rgb(T.kepalaJadual), NAVY2 = rgb(dark(T.kepalaJadual)), TEAL = rgb(T.graf),
              GROUP = [232, 238, 243], SPACER = [71, 85, 105];

        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();

        // ── Watermark (faded HIES logo) — painted FIRST so the title and table
        //    render on top of it. jsPDF has no z-order: paint order is stacking
        //    order, so "behind" = drawn before. 0.14 keeps the blue/green visible.
        const wm = await loadImage(hiesWatermark, 0.14);
        const wmH = 110; // mm
        const wmW = wm.width * (wmH / wm.height);
        const drawWatermark = () =>
          doc.addImage(wm.dataUrl, "PNG", (pageW - wmW) / 2, (pageH - wmH) / 2, wmW, wmH);
        drawWatermark(); // page 1 — later pages via autoTable's willDrawPage hook

        // ── Logo: Jata Negara crest + config-driven title (services/exportHeader) ──
        await addJataHeaderPdf(doc, pageW);

        doc.setFont("times", "bold"); doc.setFontSize(13);
        doc.setTextColor(NAVY[0], NAVY[1], NAVY[2]);
        doc.text(await surveyTitle(), pageW / 2, 35, { align: "center" });
        doc.setFontSize(11); doc.setTextColor(TEAL[0], TEAL[1], TEAL[2]);
        doc.text("PENDAPATAN ISI RUMAH BULANAN PENENGAH", pageW / 2, 41.5, { align: "center" });

        doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(90);
        doc.text(
          `Pilihan: ${this.negeri === "ALL" ? "Malaysia" : this.scopeName}     |     Pusingan: ${this.ddLabel}     |     Data: ${this.wajaranLabel}     |     Dijana: ${new Date().toLocaleString("en-MY")}`,
          14, 48
        );

        const YEARS = this.years;
        // real year-pairs, or a single "—" placeholder column (single cycle)
        const CAGR_COLS = this.cagrPairs.length
          ? this.cagrPairs.map(p => ({ label: `${p.from} - ${p.to}`, value: row => this.pf(row.cagr[p.from + "-" + p.to]) }))
          : [{ label: "—", value: () => "—" }];
        const nCols = this.colCount;
        const head = [
          [
            { content: "KATEGORI", rowSpan: 2 },
            ...YEARS.map(y => ({ content: `${y} (RM)`, rowSpan: 2 })),
            { content: "KADAR PERTUMBUHAN TAHUNAN DIKOMPAUN (CAGR) (%)", colSpan: CAGR_COLS.length },
          ],
          CAGR_COLS.map(c => c.label),
        ];
        const kinds = [];
        const body = this.visibleRows.map(r => {
          if (r._isGroup) { kinds.push("group"); return [{ content: (r.state || "").toUpperCase(), colSpan: nCols }]; }
          if (r._isSpacer) { kinds.push("spacer"); return [{ content: "", colSpan: nCols }]; }
          kinds.push(r._isTotal ? "total" : (r._isStrata || r._isKaum || r._isDaerah) ? (r.indent ? "subindent" : "sub") : "state");
          return [
            r.state,
            ...YEARS.map(y => this.rm(r.values[y])),
            ...CAGR_COLS.map(c => c.value(r)),
          ];
        });

        doc.autoTable({
          head, body, startY: 52,
          // pages the table adds get their watermark painted before their content
          // too (page 1 already has it — drawn above, before the header block)
          willDrawPage: d => { if (d.pageNumber > 1) drawWatermark(); },
          // fillColor:false = TRANSPARENT body cells, so the watermark painted
          // beneath the table shows through (the default theme paints every cell
          // white, which was hiding it). Meaningful fills (totals, group rows)
          // are still applied opaquely in didParseCell.
          theme: "plain",
          styles: { font: "helvetica", fontSize: 7.5, cellPadding: 1.6, halign: "right", lineColor: [226, 232, 240], lineWidth: 0.1, valign: "middle", fillColor: false },
          headStyles: { fillColor: HEAD, textColor: 255, halign: "center", fontSize: 7, valign: "middle" },
          columnStyles: { 0: { halign: "left", fontStyle: "bold" } },
          didParseCell: d => {
            if (d.section === "head" && d.row.index === 1) d.cell.styles.fillColor = NAVY2;
            if (d.section === "body") {
              const kind = kinds[d.row.index];
              if (kind === "group") { d.cell.styles.fillColor = GROUP; d.cell.styles.textColor = [11, 31, 58]; d.cell.styles.fontStyle = "bold"; d.cell.styles.halign = "left"; }
              else if (kind === "spacer") { d.cell.styles.fillColor = SPACER; d.cell.styles.cellPadding = 0; d.cell.styles.fontSize = 1; d.cell.styles.minCellHeight = 1; }
              else if (kind === "total") { d.cell.styles.fillColor = TEAL; d.cell.styles.textColor = 255; d.cell.styles.fontStyle = "bold"; }
              else if (kind === "sub" || kind === "subindent") {
                // no fill — transparent like the plain rows, so the watermark
                // shows through the strata / kaum / daerah breakdowns too
                if (d.column.index === 0) {
                  d.cell.styles.fontStyle = "normal";
                  if (kind === "subindent") d.cell.styles.cellPadding = { left: 6, top: 1.6, right: 1.6, bottom: 1.6 };
                }
              }
            }
          },
        });

        // page numbers (e.g. "1/10") bottom-right on every page — the watermark
        // is painted per page BEFORE the content (see drawWatermark above)
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(120);
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.text(`${i}/${pageCount}`, pageW - 14, pageH - 6, { align: "right" });
        }

        doc.save(`${this.exportFileName}.pdf`);
      } catch (err) {
        this.snackbar = { show: true, message: err.message || "Eksport PDF gagal.", color: "error", timeout: 4000 };
      }
      this.exportingPdf = false;
    },
  },
};
</script>

<style scoped>
/* ---------- Bar penapis ----------
   Warna diambil daripada palet yang dikongsi `paparanutama`, sidebar
   (MenuHeader) dan <PageHeader> — teal-700 #037769, wash #eef8f6,
   hair #dbeeeb — supaya penapis tidak lagi kelihatan seperti kawalan
   Vuetify lalai (biru/kelabu) di atas halaman teal. */
.filter-bar {
  background: #f7fcfb;
  border-top: 1px solid #dbeeeb;
  border-bottom: 1px solid #dbeeeb;
}

/* Label penapis — sama gaya dengan `.nav-eyebrow` dalam sidebar. */
.fld__label {
  font-size: var(--fs-3xs);
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #7aa8a2;
  line-height: 1.4;
  margin-bottom: 5px;
  white-space: nowrap;
}

/* Medan outlined: birai teal lembut, menyerlah teal-700 bila fokus. */
.filter-bar ::v-deep .v-input fieldset {
  border-color: #cfeae7;
}
.filter-bar ::v-deep .v-input--is-focused fieldset {
  border-color: #037769 !important;
  border-width: 1px;
}
.filter-bar ::v-deep .v-select__selection--comma {
  color: #21403c;
}
.filter-bar ::v-deep .v-input__append-inner .v-icon {
  color: #7aa8a2;
}

/* Butang "Tetapkan Semula" — dulu kelabu neutral; kini senada teal. */
.reset-btn.v-btn--outlined {
  border-color: #cfeae7;
  color: #037769;
  letter-spacing: normal;
}
.reset-btn.v-btn--outlined:hover {
  background: #eef8f6;
  border-color: #037769;
}

/* Compact Pusingan range select — one dropdown, "01 - 01" … "01 - 12" */
.pusingan-range .pusingan-sel {
  width: 116px;
  flex: 0 0 116px;
}
.pusingan-range ::v-deep .v-select__selections {
  flex-wrap: nowrap;
}
.pusingan-range ::v-deep .v-select__selection {
  overflow: visible;
  white-space: nowrap;
}
.pusingan-range ::v-deep .v-input__slot {
  padding: 0 8px !important;
}
.pusingan-range ::v-deep .v-input__append-inner {
  margin-left: 0;
}

.bulanan-penengah {
  padding: 16px;
  margin-left: -12px;
  margin-right: -12px;
}

/* Toggle Wajaran / Tanpa Wajaran: birai sepadan dengan medan outlined di
   sebelahnya; pilihan aktif = teal pejal (dulu biru `primary` Vuetify). */
.data-toggle {
  border: 1px solid #cfeae7;
  border-radius: 4px;
  overflow: hidden;
  background: #ffffff;
}
.data-toggle ::v-deep .v-btn {
  border: none !important;
  letter-spacing: normal;
  color: #4a8a84;
  background: transparent;
}
.data-toggle ::v-deep .v-btn.v-btn--active {
  background: #037769 !important;
  color: #ffffff !important;
}
/* Vuetify meletakkan lapisan legap pada butang aktif — ia mengaburkan teal. */
.data-toggle ::v-deep .v-btn.v-btn--active .v-btn__overlay,
.data-toggle ::v-deep .v-btn.v-btn--active::before {
  opacity: 0 !important;
}

/* grouped-header detailed table */
::v-deep .bp-table > .v-data-table__wrapper { overflow-x: auto; }
::v-deep .bp-table table { border-collapse: separate; border-spacing: 0; width: 100%; font-size: var(--fs-sm); white-space: nowrap; min-width: 900px; }
/* fixed header-row height so the two header rows line up exactly */
::v-deep .bp-table thead tr { height: 42px; }
::v-deep .bp-table thead th {
  background: var(--warna-kepala-jadual, #1B7E91); color: #fff !important; font-weight: 600; padding: 8px 12px;
  text-align: center !important; border: 1px solid #2a8ea1; vertical-align: middle;
  font-size: var(--fs-sm) !important; line-height: 1.3;
  /* sticky relative to the table's own scroll wrapper — top:0 so it never
     overlaps the first body row (MALAYSIA) */
  position: sticky; top: 0; z-index: 4;
}
/* second header row sticks just below the first (row1 height = 42px) */
::v-deep .bp-table thead tr:nth-child(2) th { background: var(--warna-kepala-jadual-2, #15606F); top: 42px; z-index: 4; }
::v-deep .bp-table thead th.cat { left: 0; z-index: 6; }
::v-deep .bp-table tbody td {
  padding: 10px 12px; border: 1px solid #E2E8F0; text-align: right; color: #0B1F3A; height: auto; font-size: var(--fs-sm);
}
::v-deep .bp-table tbody td.cat {
  text-align: left; font-weight: 600; position: sticky; left: 0; background: #fff; z-index: 2;
}
::v-deep .bp-table tbody td.cagr-empty { text-align: center; color: #94A3B8; }
::v-deep .bp-table tbody tr:nth-child(even) td { background: #FAFCFE; }
::v-deep .bp-table tbody tr:nth-child(even) td.cat { background: #FAFCFE; }
::v-deep .bp-table tbody tr:hover td { background: #EFF7F7 !important; }
::v-deep .bp-table tbody tr.total td { background: var(--warna-graf, #0EA5A4) !important; color: #fff; font-weight: 700; border-color: var(--warna-graf-gelap, #0B827F); }
::v-deep .bp-table tbody tr.group td.cat {
  background: #E8EEF3 !important; color: #0B1F3A; font-weight: 700; text-transform: uppercase;
  letter-spacing: .4px; font-size: var(--fs-xs); text-align: left; position: static;
}
::v-deep .bp-table tbody tr.sub td { background: #F4F8FA !important; }
::v-deep .bp-table tbody tr.sub td.cat { background: #F4F8FA !important; padding-left: 24px; font-weight: 600; }
/* Bumiputera ethnic sub-groups (Sabah/Sarawak) inset one more level */
::v-deep .bp-table tbody tr.sub td.cat.indent { padding-left: 44px; font-weight: 400; }
::v-deep .bp-table tbody tr.spacer td { background: #475569 !important; padding: 0; height: 6px; border: none; }
</style>
